/**
 * GBUX Games lobby — 2×2 play-ledger casino hub.
 * Route: /gbuxgames  (also /gbux-games)
 */
import type { CSSProperties } from "react";
import { useLocation } from "wouter";
import { useWallet, useWalletState } from "@/contexts/wallet-context";
import { formatPlayGbux } from "@shared/gbux-math";
import { BUDB_CHARACTER } from "@shared/budb-character";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Coins,
  Spade,
  LayoutGrid,
  Swords,
  Wallet,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import "@/styles/gbux-games.css";

type GameCard = {
  id: string;
  title: string;
  tagline: string;
  href: string;
  art: string;
  artFallback: string;
  accent: string;
  accentSoft: string;
  meta: string;
  badge?: string;
  icon: typeof Spade;
};

const GAMES: GameCard[] = [
  {
    id: "slots",
    title: "Ethereal Treasure",
    tagline: "5×5 · 25 lines · Racalvin vault",
    href: "/slots",
    art: "/slots/gen/cabinet-mesh.jpg",
    artFallback: "/slots/craftpix/cabinet-classic-sevens.jpg",
    accent: "#f59e0b",
    accentSoft: "rgba(245, 158, 11, 0.22)",
    meta: "Play GBUX · CSPRNG reels",
    icon: Coins,
  },
  {
    id: "blackjack",
    title: "Blackjack",
    tagline: "3 decks · up to 3 hands · 3:2",
    href: "/blackjack",
    art: "/media/blackjack/table-web.jpg",
    artFallback: "/blackjack/table/table_1.png",
    accent: "#34d399",
    accentSoft: "rgba(52, 211, 153, 0.2)",
    meta: "Play GBUX · fair shoe",
    icon: Spade,
  },
  {
    id: "holdem",
    title: "Hold'em tables",
    tagline: "Cash NL · multi-table lobby",
    href: "/lobby",
    art: "/media/lobby-table-hero.jpg",
    artFallback: "/media/felt-budb-blue.jpg",
    accent: "#60a5fa",
    accentSoft: "rgba(96, 165, 250, 0.2)",
    meta: "Live DO tables · edge ledger",
    icon: LayoutGrid,
  },
  {
    id: "hu-budb",
    title: "HU vs BUDB",
    tagline: `Heads-up vs ${BUDB_CHARACTER.name}`,
    href: `/table/${BUDB_CHARACTER.vsTableId}`,
    art: "/media/table-thumb-hu.png",
    artFallback: "/media/felt-budb-green.jpg",
    accent: "#fde68a",
    accentSoft: "rgba(234, 179, 8, 0.28)",
    meta: "NL 1/2 · BUDB voice · agent AI seat",
    badge: "Featured",
    icon: Swords,
  },
];

export default function GbuxGamesPage() {
  const [, setLocation] = useLocation();
  const { playGbux, connected } = useWallet();
  const { openWalletPanel } = useWalletState();

  return (
    <div className="gbux-games-page">
      <div className="gbux-games-page__bg" aria-hidden />
      <div className="gbux-games-page__orb gbux-games-page__orb--a" aria-hidden />
      <div className="gbux-games-page__orb gbux-games-page__orb--b" aria-hidden />

      <header className="gbux-games-header">
        <div className="gbux-games-header__inner">
          <div className="flex min-w-0 items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 border-white/15 bg-black/40 text-zinc-100 hover:bg-white/10"
              onClick={() => setLocation("/lobby")}
            >
              <ArrowLeft className="mr-1 h-4 w-4" /> Tables
            </Button>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-bold tracking-wide text-amber-100 sm:text-xl">
                GBUX Games
              </h1>
              <p className="truncate text-[11px] text-zinc-500 sm:text-xs">
                2×2 casino hub · one play wallet
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <div className="gbux-play-chip">
              <span className="text-[10px] uppercase tracking-wide text-zinc-500">
                Play
              </span>
              <strong className="text-amber-300 tabular-nums">
                {formatPlayGbux(playGbux ?? 0)}
              </strong>
            </div>
            <Button
              size="sm"
              className="bg-amber-600 font-semibold text-black hover:bg-amber-500"
              onClick={() =>
                openWalletPanel(connected ? "deposit" : "connect")
              }
            >
              <Wallet className="mr-1 h-4 w-4" />
              {connected ? "Fund" : "Connect"}
            </Button>
          </div>
        </div>
      </header>

      <main className="gbux-games-main">
        <section className="gbux-games-hero">
          <div className="gbux-games-hero__badge">
            <Sparkles size={14} />
            Play ledger · server-dealt
          </div>
          <p className="gbux-games-hero__copy">
            Pick a game. All four debit the same{" "}
            <strong className="text-emerald-300">play GBUX</strong> ledger —
            never client RNG for money.
          </p>
        </section>

        <div className="gbux-games-grid">
          {GAMES.map((g) => {
            const Icon = g.icon;
            return (
              <button
                key={g.id}
                type="button"
                className="gbux-tile group"
                data-game={g.id}
                onClick={() => setLocation(g.href)}
                style={
                  {
                    "--tile-accent": g.accent,
                    "--tile-accent-soft": g.accentSoft,
                  } as CSSProperties
                }
              >
                <div
                  className="gbux-tile__art"
                  style={{
                    backgroundImage: `url(${g.art}), url(${g.artFallback})`,
                  }}
                >
                  <div className="gbux-tile__art-shade" />
                  {g.badge ? (
                    <span className="gbux-tile__badge">{g.badge}</span>
                  ) : null}
                  <span className="gbux-tile__id">
                    <Icon size={14} strokeWidth={2.4} />
                    {g.id}
                  </span>
                </div>
                <div className="gbux-tile__body">
                  <h2 className="gbux-tile__title">{g.title}</h2>
                  <p className="gbux-tile__tagline">{g.tagline}</p>
                  <div className="gbux-tile__footer">
                    <span className="gbux-tile__meta">{g.meta}</span>
                    <span className="gbux-tile__cta">
                      Play
                      <ChevronRight
                        size={16}
                        className="transition group-hover:translate-x-0.5"
                      />
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <footer className="gbux-games-links">
          <span className="text-zinc-600">Direct</span>
          <button type="button" onClick={() => setLocation("/slots")}>
            /slots
          </button>
          <button type="button" onClick={() => setLocation("/blackjack")}>
            /blackjack
          </button>
          <button type="button" onClick={() => setLocation("/lobby")}>
            /lobby
          </button>
          <button
            type="button"
            onClick={() => setLocation(`/table/${BUDB_CHARACTER.vsTableId}`)}
          >
            /table/{BUDB_CHARACTER.vsTableId}
          </button>
        </footer>
      </main>
    </div>
  );
}
